import { Form, Formik, FormikProps } from 'formik';
import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { SourceType } from '../../../../../../../../typings/Integration.interface';
import { APISource } from '../../../../../../../../typings/Source.interface';
import Button from '../../../../common/base/Button';
import Container from '../../../../common/base/Container';
import Divider from '../../../../common/base/Divider';
import Text from '../../../../common/base/Text';
import { useDialog } from '../../../../common/Dialog';
import { Div } from '../../../../common/helpers/StyledUtils';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { TeamPermission } from '../../../../contexts/TeamPermissionContext';
import { DashboardContext } from '../../DashboardContext';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog';
import resource_details_form_props from '../Forms/resource_details';
import source_configuration_form_props, { SourceConfigurationFormValues } from '../Forms/source_configuration';
import SaveButton from '../SaveButton';
export const SourceSettings = ({
  source,
  mutate
}: {
  source: APISource;
  mutate: (data: any) => any;
}) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const formRef = useRef<FormikProps<SourceConfigurationFormValues>>(null);
  const {
    source_types
  } = useContext(DashboardContext);
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  const history = useHistory();
  const [type, setType] = useState<SourceType | null>(null);
  useEffect(() => {
    const new_type = source?.type || formRef?.current?.values?.type;
    setType(new_type || null);
  }, [source?.type, formRef?.current?.values?.type]);
  const onUpdate = async values => HookdeckAPI.sources.update(source.id, values).then(source_updated => {
    const source_merged = {
      ...source,
      ...source_updated
    };
    mutate(source_merged);
    addToast('success', 'Source saved');
    return source_merged;
  }).catch(e => {
    addToast('error', `An error occurred while saving the source${e.response?.data[0] ? `: ${e.response.data[0]}` : ''}`);
    throw e;
  });
  const onEnable = async () => {
    HookdeckAPI.sources.enable(source.id).then(source_0 => {
      mutate(source_0);
      addToast('success', 'Source enabled');
    }).catch(() => {
      addToast('error', 'Failed to enable source');
    });
  };
  const onDisable = async () => {
    showDialog(() => {
      HookdeckAPI.sources.disable(source.id).then(source_1 => {
        mutate(source_1);
        addToast('success', 'Source disabled');
      }).catch(() => {
        addToast('error', 'Failed to disable source');
      });
    }, () => null, {
      title: 'Disable source',
      submit_label: 'Disable',
      cancel_label: 'Cancel',
      message: 'By disabling this source, all connections associated with this source will also be disabled. Are you sure?'
    });
  };
  const onDelete = async () => {
    showDialog(() => {
      HookdeckAPI.sources.delete(source.id).then(() => {
        addToast('success', 'Source deleted');
        history.push('/sources');
      }).catch(() => {
        addToast('error', 'Failed to delete source');
      });
    }, () => null, {
      danger: true,
      title: 'Delete source',
      submit_label: 'Delete',
      cancel_label: 'Keep source',
      message: <ConfirmDeleteDialog text="By deleting this source, no requests will be accepted or delivered anymore. All connections associated with this source will also be deleted. Are you sure?" />
    });
  };
  return <Div>
      <Container medium left>
        <TeamPermission role="member">
          <Formik key={source.id} initialValues={resource_details_form_props.getInitialValues(source)} validate={values_0 => resource_details_form_props.validate(values_0, name => name === source.name ? Promise.resolve(false) : HookdeckAPI.sources.nameIsUsed(name))} onSubmit={(values_1, {
          resetForm
        }) => onUpdate(resource_details_form_props.postprocessValues(values_1)).then(source_2 => {
          resetForm({
            values: resource_details_form_props.getInitialValues(source_2)
          });
        })}>
            <Form>
              <Text bold size="l" m={{
              t: 6,
              b: 4
            }}>
                Details
              </Text>
              <resource_details_form_props.Fields prefix="" placeholder="shopify-prod" name_prefix="Source" show_description description_help="Optionally provide a description for this resource." help="Used to identify this source while using Hookdeck." />
              <SaveButton m={{
              t: 5
            }} />
            </Form>
          </Formik>
        </TeamPermission>
      </Container>
      <Div m={{
      x: 6,
      t: 10,
      b: 8
    }}>
        <Divider />
      </Div>
      <Container medium left>
        <TeamPermission role="member">
          <Text as="h2" size="l" bold m={{
          b: 4
        }}>
            Configuration
          </Text>

          <Formik key={`${source.id}-${type}`} innerRef={formRef} initialValues={source_configuration_form_props.getInitialValues({
          ...source,
          type: source.type || type || undefined
        })} validate={v => source_configuration_form_props.validate(v as SourceConfigurationFormValues, source_types)} onSubmit={async (values_2, {
          resetForm: resetForm_0
        }) => {
          return onUpdate(source_configuration_form_props.postprocessValues(values_2 as SourceConfigurationFormValues, source_types)).then(() => resetForm_0({
            values: values_2
          }));
        }}>
            <Form>
              <source_configuration_form_props.Fields prefix="" disable_name show_all advanced_configuration_display="section" />
              <SaveButton m={{
              t: 5
            }} />
            </Form>
          </Formik>
        </TeamPermission>
      </Container>
      <Div m={{
      x: 6,
      t: 10,
      b: 8
    }}>
        <Divider />
      </Div>
      <Container medium left m={{
      b: 20
    }}>
        <TeamPermission role="member">
          <Text as="h2" size="l" bold m={0}>
            {source.disabled_at ? 'Enable' : 'Disable'} source
          </Text>
          <Text as="p" muted m={{
          b: 4
        }}>
            {source.disabled_at ? 'Enabling your source will allow new incoming requests for your Source URL.' : 'Rejects all incoming requests to your source URL.'}
          </Text>
          <Button.Permission outline onClick={source.disabled_at ? onEnable : onDisable} icon={source.disabled_at ? 'add_circle' : 'disable'}>
            {source.disabled_at ? 'Enable' : 'Disable'} source
          </Button.Permission>
          <Text as="h2" size="l" bold m={{
          t: 10,
          b: 0
        }}>
            Delete source
          </Text>
          <Text as="p" muted m={{
          b: 4
        }}>
            Deletes this source and all associated connections. Requests to this source URL will
            return an HTTP 410 and will not be logged.
          </Text>
          <Button.Permission danger outline onClick={onDelete} icon="delete">
            Delete source
          </Button.Permission>
        </TeamPermission>
      </Container>
    </Div>;
};